import { Trans } from '@lingui/react/macro';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import {
  createQueryCloudConnectPaymentHistory,
  createQueryCloudConnectSubscription,
  createQueryPaymentHistory,
  createQuerySubscriptions,
  shouldFallbackToStoreApi,
  useCloudConnectPaymentHistory,
  usePaymentHistory,
  useCloudConnectSubscription,
  useSubscriptionSuspense,
} from 'src/api/queries';
import { CloudConnectSubscriptionCanceledNotification } from 'src/apps/manageSubscription/app/components/CloudConnectSubscriptionCanceledNotification';
import { PausedSubscriptionNotification } from 'src/apps/manageSubscription/app/components/PausedNotification';
import { SubscriptionCanceledNotification } from 'src/apps/manageSubscription/app/components/SubscriptionCanceledNotification';
import { Spinner } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { PillNav, PillTab } from 'src/components/PillNav';
import { createPageMetaTitle } from 'src/utils/createPageMetaTitle';
import { URLS } from 'src/utils/urls/urls';

export const Route = createFileRoute('/_auth/_layout/subscriptions')({
  staticData: { title: createPageMetaTitle('Subscription') },
  loader: async ({ context }) => {
    try {
      const cloudConnectSubscription =
        await context.queryClient.ensureQueryData(
          createQueryCloudConnectSubscription()
        );

      if (!shouldFallbackToStoreApi(cloudConnectSubscription)) {
        await context.queryClient.ensureQueryData(
          createQueryCloudConnectPaymentHistory()
        );
        return { useStoreApi: false };
      }
    } catch (error) {
      if (!shouldFallbackToStoreApi(null, error)) throw error;
    }

    await Promise.all([
      context.queryClient.ensureQueryData(createQueryPaymentHistory()),
      context.queryClient.ensureQueryData(createQuerySubscriptions()),
    ]);
    return { useStoreApi: true };
  },
  component: () => <SubscriptionPage />,
});

const SubscriptionPage = () => {
  const { useStoreApi } = Route.useLoaderData();
  const cloudConnectSubscription = useCloudConnectSubscription({
    enabled: !useStoreApi,
  });

  if (useStoreApi) {
    return <StoreSubscriptionPage />;
  }

  if (cloudConnectSubscription.isPending) {
    return <CloudConnectSubscriptionLayout subscription={null} isPending />;
  }

  const subscription = cloudConnectSubscription.data ?? null;

  if (shouldFallbackToStoreApi(subscription, cloudConnectSubscription.error)) {
    return <StoreSubscriptionPage />;
  }

  if (cloudConnectSubscription.error) throw cloudConnectSubscription.error;

  return <CloudConnectSubscriptionLayout subscription={subscription} />;
};

const StoreSubscriptionPage = () => {
  const subscription = useSubscriptionSuspense();
  const paymentHistory = usePaymentHistory();

  const isSeatManagement =
    subscription.data?.type === 'stripe' &&
    subscription.data.managementType === 'seats';

  const access = subscription.data?.access;

  const canSeeMembersPage = isSeatManagement && access?.canSeeMembers;

  const hasPaymentHistory = (paymentHistory.data?.length ?? 0) > 0;

  const canSeePaymentPage =
    hasPaymentHistory ||
    access?.canSeeFailedPaymentInformation ||
    access?.canSeePaymentInformation ||
    access?.canSeeNextInvoiceAmount ||
    access?.canSeePaymentHistory ||
    access?.canChangePaymentInterval;

  const showNavbar = canSeeMembersPage || canSeePaymentPage;

  return (
    <AppPage.Container>
      <div className="mx-auto max-w-l2xl px-16 lm:px-32">
        <SubscriptionCanceledNotification />
      </div>

      <AppPage.Header
        title={<Trans>Subscription</Trans>}
        className="max-w-l2xl"
      />

      <AppPage.Content className="max-w-l2xl">
        {showNavbar && (
          <PillNav>
            <PillTab
              to={URLS.SUBSCRIPTION_PLAN}
              data-cy="plan-tab-link"
              componentLocation={ComponentLocations.MANAGE_CONNECT.MAIN}
            >
              <Trans>Plan</Trans>
            </PillTab>
            <PillTab
              to={URLS.SUBSCRIPTION_PAYMENT}
              data-cy="payment-tab-link"
              componentLocation={ComponentLocations.MANAGE_CONNECT.MAIN}
            >
              <Trans>Payment</Trans>
            </PillTab>
            {canSeeMembersPage && (
              <PillTab
                to={URLS.SUBSCRIPTION_MEMBERS}
                data-cy="members-tab-link"
                componentLocation={ComponentLocations.MANAGE_CONNECT.MAIN}
              >
                <Trans>Members</Trans>
              </PillTab>
            )}
          </PillNav>
        )}
        <PausedSubscriptionNotification subscription={subscription.data} />

        <Outlet />
      </AppPage.Content>
    </AppPage.Container>
  );
};

export const CloudConnectSubscriptionLayout = ({
  isPending = false,
  subscription,
}: {
  isPending?: boolean;
  subscription: CloudConnectSubscription | null;
}) => {
  const paymentHistory = useCloudConnectPaymentHistory();
  const hasPaymentHistory = (paymentHistory.data?.length ?? 0) > 0;
  const showNavbar =
    hasPaymentHistory ||
    (!!subscription && subscription.productId !== 'connect_legacy');

  return (
    <AppPage.Container>
      <div className="mx-auto max-w-l2xl px-16 lm:px-32">
        {/* Business subscriptions use the enterprise cancellation workflow. */}
        {subscription?.productId === 'connect' && (
          <CloudConnectSubscriptionCanceledNotification
            subscription={subscription}
          />
        )}
      </div>

      <AppPage.Header
        title={<Trans>Subscription</Trans>}
        className="max-w-l2xl"
      />

      <AppPage.Content className="max-w-l2xl">
        {showNavbar && (
          <PillNav>
            <PillTab
              to={URLS.SUBSCRIPTION_PLAN}
              data-cy="plan-tab-link"
              componentLocation={ComponentLocations.MANAGE_CONNECT.MAIN}
            >
              <Trans>Plan</Trans>
            </PillTab>
            <PillTab
              to={URLS.SUBSCRIPTION_PAYMENT}
              data-cy="payment-tab-link"
              componentLocation={ComponentLocations.MANAGE_CONNECT.MAIN}
            >
              <Trans>Payment</Trans>
            </PillTab>
          </PillNav>
        )}

        {isPending ? <Spinner /> : <Outlet />}
      </AppPage.Content>
    </AppPage.Container>
  );
};
